import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  updateCompanyDetails,
  updateContactAndAddress,
  updateBranding,
  updateLegalDocs,
  getProfile
} from '../../ApiController/ApiService';
import centerLogo from '../../assets/bird.svg';
import letter from '../../assets/letter.svg';
import badge from '../../assets/badge.svg';
import Swal from 'sweetalert2';
import { useDashboard } from '../Context/Dashboardcontext';

const CompanyProfile = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('Company Details');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [companyData, setCompanyData] = useState({
    Id: '',
    fullName: '',
    websiteUrl: '',
    bio: '',
    companyType: '',
    establishedDate: '',
    email: '',
    supportContactNumber: '',
    country: '',
    city: '',
    pinCode: '',
    timeZone: '',
    invoiceFooterText: 'e.g. company disclaimer',
    companyRegistrationNumber: '',
    gstVat: '',
    raw: []
  });

  const [subscriptionData, setSubscriptionData] = useState({
    planName: '',
    status: '',
    nextBillingDate: '',
    paymentMethod: '',
    price: 0,
    planDescription: '',
    activePackageId: null, // Store the ID of the active package here
    isFree: false,
    isAiPackage: false
  });

  const [brandingFiles, setBrandingFiles] = useState({
    logo: null,
    secondaryLogo: null,
    customLetterHead: null
  });

  const [legalDocsFiles, setLegalDocsFiles] = useState({
    companyLicense: null,
    taxRegistrationDoc: null,
    businessInsuranceProof: null
  });

  const [existingDocUrls, setExistingDocUrls] = useState({
    companyLicense: '',
    taxRegistrationDoc: '',
    businessInsuranceProof: ''
  });

  const tabs = {
    'Company Details': 'companyDetails',
    'Contact & Address': 'contactAddress',
    'Legal & Registration Docs': 'legalDocs',
    'Subscription Overview': 'subscription',
  };

  const navigate = useNavigate();
  const { FetchProfile, userProfile, cancelPackageMemberShip, CancelForAiPackage } = useDashboard();

  useEffect(() => {
    FetchProfile();
  }, []);

  const fetchProfileData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getProfile();
      const profile = response; // Directly use the response object based on your JSON structure

      if (!profile) {
        throw new Error("Profile data could not be retrieved from the API.");
      }

      setCompanyData(prevData => ({
        ...prevData,
        Id: profile.Id || 'N/A',
        fullName: profile.fullName || '',
        websiteUrl: profile.websiteUrl || '',
        bio: profile.bio || '',
        companyType: profile.companyType || '',
        establishedDate: profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
        email: profile.email || '',
        supportContactNumber: profile.supportContactNumber || profile.mobileNumber || 'N/A',
        country: profile.country || '',
        city: profile.city || '',
        pinCode: profile.pinCode || '',
        timeZone: profile.timeZone || '',
        companyRegistrationNumber: profile.companyRegistrationNumber || '',
        gstVat: profile.gstVat || '',
        raw: profile
      }));

      setExistingDocUrls({
        companyLicense: profile.companyLicense || '',
        taxRegistrationDoc: profile.taxRegistrationDoc || '',
        businessInsuranceProof: profile.businessInsuranceProof || ''
      });

      // --- LOGIC CHANGE STARTS HERE ---
      // Determine which package to show: AI Package takes priority if it exists
      const activePackage = profile.aiPackageId || profile.packageId;
      
      // Determine the expiration date based on which package is active
      // If AI package is active, use aiPackageExpiration, otherwise packageExpiration
      const activeExpirationDate = profile.aiPackageId 
        ? profile.aiPackageExpiration 
        : profile.packageExpiration;

      setSubscriptionData({
        planName: activePackage?.name || activePackage?.nameDescription || (typeof activePackage === 'string' ? 'Active Plan' : '-'),
        status: profile.userStatus || '-',
        nextBillingDate: activeExpirationDate
          ? new Date(activeExpirationDate).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric'
          })
          : t('common.notAvailable'),
        paymentMethod: profile.paymentMethod || 'Card',
        price: activePackage?.price || 0,
        planDescription: activePackage?.description || 'Default plan description.',
        activePackageId: activePackage?._id || (typeof activePackage === 'string' ? activePackage : null), // Save the ID for the cancel button
        isFree: activePackage?.isFree || false,
        isAiPackage: !!profile.aiPackageId
      });
      // --- LOGIC CHANGE ENDS HERE ---

    } catch (err) {
      console.error('Error fetching profile data:', err);
      if (err.message && err.message.includes('Unauthorized')) {
        navigate('/login');
      } else {
        setError(t('companyProfile.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [navigate, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fileType, stateSetter) => {
    const file = e.target.files[0];
    if (file) {
      stateSetter(prev => ({ ...prev, [fileType]: file }));
    }
  };

  // ... (handleSubmitCompanyDetails, handleSubmitContactAddress, handleSubmitBranding, handleSubmitLegalDocs remain the same)

  const handleSubmitCompanyDetails = async (e) => {
    e.preventDefault();
    try {
      await updateCompanyDetails({
        fullName: companyData.fullName,
        websiteUrl: companyData.websiteUrl,
        bio: companyData.bio,
        companyType: companyData.companyType,
        establishedDate: companyData.establishedDate
      });
      Swal.fire({ icon: 'success', title: t('updateSuccess'), showConfirmButton: false, timer: 2000 });
    } catch (error) {
      Swal.fire({ icon: 'error', title: t('common.updateFailed'), text: error?.message || '' });
    }
  };

  const handleSubmitContactAddress = async (e) => {
    e.preventDefault();
    try {
      await updateContactAndAddress({
        supportContactNumber: companyData.supportContactNumber,
        country: companyData.country,
        city: companyData.city,
        pinCode: companyData.pinCode,
        timeZone: companyData.timeZone
      });
      Swal.fire({ icon: 'success', title: t('updateSuccess'), showConfirmButton: false, timer: 2000 });
    } catch (error) {
      Swal.fire({ icon: 'error', title: t('common.updateFailed'), text: error?.message || '' });
    }
  };

  const handleSubmitLegalDocs = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('companyRegistrationNumber', companyData.companyRegistrationNumber);
    formData.append('gstVat', companyData.gstVat);
    if (legalDocsFiles.companyLicense) formData.append('companyLicense', legalDocsFiles.companyLicense);
    if (legalDocsFiles.taxRegistrationDoc) formData.append('taxRegistrationDoc', legalDocsFiles.taxRegistrationDoc);
    if (legalDocsFiles.businessInsuranceProof) formData.append('businessInsuranceProof', legalDocsFiles.businessInsuranceProof);

    try {
      await updateLegalDocs(formData);
      Swal.fire({ icon: 'success', title: t('updateSuccess'), showConfirmButton: false, timer: 2000 });
    } catch (error) {
      Swal.fire({ icon: 'error', title: t('common.updateFailed'), text: error?.message || '' });
    }
  };

  // Updated Cancel Logic to use the correct ID
  const handleCancelSubscription = async () => {
    // Use the ID from our state, not the userProfile context which might be stale or wrong
    const packageId = subscriptionData.activePackageId; 
    const planName = subscriptionData.planName || t('subscription.currentPlan');
    const isAiPackage = subscriptionData.isAiPackage;

    if (!packageId) return;

    const result = await Swal.fire({
      title: t('subscription.cancelConfirmationTitle'),
      text: t('subscription.cancelConfirmationText', { plan: planName }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('subscription.yesCancel'),
      cancelButtonText: t('subscription.noKeep')
    });

    if (result.isConfirmed) {
      try {
        let cancelResult;
        if (isAiPackage) {
          cancelResult = await CancelForAiPackage(packageId);
        } else {
          cancelResult = await cancelPackageMemberShip(packageId); 
        }

        if (cancelResult?.success) {
          Swal.fire(
            t('subscription.cancelled'),
            t('subscription.cancelSuccess', { plan: planName }),
            'success'
          );
          FetchProfile();
          fetchProfileData();
        } else {
          Swal.fire(
            t('subscription.error'),
            cancelResult?.message || t('subscription.cancelError'),
            'error'
          );
        }
      } catch (error) {
        Swal.fire(
          t('subscription.error'),
          t('subscription.cancelError'),
          'error'
        );
      }
    } else {
      Swal.fire(t('subscription.cancelled'), t('subscription.stillActive'), 'info');
    }
  };

  if (isLoading) {
    return <div className="text-white text-center p-10 text-xl font-semibold">{t('companyProfile.loading')}</div>;
  }

  return (
    <div className="text-gray-800">
      <h1 className="text-2xl font-bold text-white mb-4">{t('companyProfile.title')}</h1>

      <div className="flex gap-4 bg-white pe-2 ps-2 pt-1 pb-1 border rounded-xl overflow-x-auto whitespace-nowrap">
        {Object.keys(tabs).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 px-4 rounded-lg font-semibold text-sm transition-colors duration-200 ${activeTab === tab
              ? 'bg-gradient-to-r from-[#6E00FF] to-[#A627FF] text-white'
              : 'bg-white text-[#7E31FF]'
              }`}
          >
            {t(`companyProfile.tabs.${tabs[tab]}`)}
          </button>
        ))}
      </div>

      {activeTab === 'Company Details' && (
        <form onSubmit={handleSubmitCompanyDetails}>
           {/* Form content remains same */}
           <div className="rounded-lg shadow-lg p-4 md:p-6 mt-4">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
               <div>
                 <label className="block text-sm font-medium text-white">{t('companyDetails.companyName')}</label>
                 <input type="text" name="fullName" value={companyData.fullName} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
               </div>
               <div>
                 <label className="block text-sm font-medium text-white">{t('companyDetails.website')}</label>
                 <input type="text" name="websiteUrl" value={companyData.websiteUrl} onChange={handleInputChange} placeholder={t('companyDetails.websitePlaceholder')} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
               </div>
               <div>
                 <label className="block text-sm font-medium text-white">{t('companyDetails.companyId')}</label>
                 <input type="text" value={companyData.Id} readOnly className="mt-1 w-full p-2 border border-gray-300 rounded-md mb-4 bg-gray-100" />
                 <label className="block text-sm font-medium text-white">{t('companyDetails.industryType')}</label>
                 <input type="text" name="companyType" value={companyData.companyType} onChange={handleInputChange} placeholder={t('companyDetails.industryTypePlaceholder')} className="mt-1 w-full p-2 border border-gray-300 rounded-md mb-4" />
                 <label className="block text-sm font-medium text-white">{t('companyDetails.establishedYear')}</label>
                 <input type="text" value={companyData.establishedDate} readOnly className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-gray-100" />
               </div>
               <div>
                 <label className="block text-sm font-medium text-white">{t('companyDetails.description')}</label>
                 <textarea name="bio" value={companyData.bio} onChange={handleInputChange} placeholder={t('companyDetails.descriptionPlaceholder')} className="mt-1 w-full p-2 border border-gray-300 rounded-md" rows="6" />
               </div>
             </div>
             <button type="submit" className="mt-4 bg-gradient-to-r from-[#6E00FF] to-[#A627FF] text-white px-6 py-2 rounded-lg">
               {t('common.saveChanges')}
             </button>
           </div>
        </form>
      )}

      {activeTab === 'Contact & Address' && (
         <form onSubmit={handleSubmitContactAddress}>
            {/* Form content remains same */}
            <div className="rounded-lg shadow-lg p-4 md:p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.officialEmail')}</label>
                <input type="email" name="email" value={companyData.email} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.supportContact')}</label>
                <input type="text" name="supportContactNumber" value={companyData.supportContactNumber} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-white mt-6 mb-4">{t('contactAddress.officeAddress')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.country')}</label>
                <input type="text" name="country" value={companyData.country} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.city')}</label>
                <input type="text" name="city" value={companyData.city} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.zipCode')}</label>
                <input type="text" name="pinCode" value={companyData.pinCode} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white">{t('contactAddress.timeZone')}</label>
                <input type="text" name="timeZone" value={companyData.timeZone} onChange={handleInputChange} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
            </div>
            <button type="submit" className="mt-4 bg-gradient-to-r from-[#6E00FF] to-[#A627FF] text-white px-6 py-2 rounded-lg">
              {t('common.saveChanges')}
            </button>
          </div>
         </form>
      )}

      {activeTab === 'Legal & Registration Docs' && (
         <form onSubmit={handleSubmitLegalDocs}>
            {/* Form content remains same */}
            <div className="rounded-lg shadow-lg p-4 md:p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-white">{t('legalDocs.regNo')}</label>
                <input type="text" name="companyRegistrationNumber" value={companyData.companyRegistrationNumber} onChange={handleInputChange} placeholder={t('legalDocs.regNoPlaceholder')} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white">{t('legalDocs.gstVat')}</label>
                <input type="text" name="gstVat" value={companyData.gstVat} onChange={handleInputChange} placeholder={t('legalDocs.gstVatPlaceholder')} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <label className="block text-base md:text-[19px] font-bold mb-3 text-[#1E0909]">{t('legalDocs.uploadLicense')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-md p-4">
                  <img src={legalDocsFiles.companyLicense ? URL.createObjectURL(legalDocsFiles.companyLicense) : existingDocUrls.companyLicense || letter} alt="Company License" className="mx-auto object-contain max-h-32" />
                  <input type="file" id="companyLicense" className="hidden" onChange={(e) => handleFileChange(e, 'companyLicense', setLegalDocsFiles)} />
                  <label htmlFor="companyLicense" className="mt-3 bg-[#797979] text-white px-4 py-1 rounded-md cursor-pointer text-sm md:text-base">
                    {legalDocsFiles.companyLicense || existingDocUrls.companyLicense ? t('legalDocs.changeFile') : t('legalDocs.uploadFile')}
                  </label>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <label className="block text-base md:text-[19px] font-bold mb-3 text-[#1E0909]">{t('legalDocs.uploadTaxDoc')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-md p-4">
                  <img src={legalDocsFiles.taxRegistrationDoc ? URL.createObjectURL(legalDocsFiles.taxRegistrationDoc) : existingDocUrls.taxRegistrationDoc || letter} alt="Tax Registration" className="mx-auto object-contain max-h-32" />
                  <input type="file" id="taxRegistrationDoc" className="hidden" onChange={(e) => handleFileChange(e, 'taxRegistrationDoc', setLegalDocsFiles)} />
                  <label htmlFor="taxRegistrationDoc" className="mt-3 bg-[#797979] text-white px-4 py-1 rounded-md cursor-pointer text-sm md:text-base">
                    {legalDocsFiles.taxRegistrationDoc || existingDocUrls.taxRegistrationDoc ? t('legalDocs.changeFile') : t('legalDocs.uploadFile')}
                  </label>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center shadow-md">
                <label className="block text-base md:text-[19px] font-bold mb-3 text-[#1E0909]">{t('legalDocs.uploadInsurance')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-md p-4">
                  <img src={legalDocsFiles.businessInsuranceProof ? URL.createObjectURL(legalDocsFiles.businessInsuranceProof) : existingDocUrls.businessInsuranceProof || letter} alt="Business Insurance" className="mx-auto object-contain max-h-32" />
                  <input type="file" id="businessInsuranceProof" className="hidden" onChange={(e) => handleFileChange(e, 'businessInsuranceProof', setLegalDocsFiles)} />
                  <label htmlFor="businessInsuranceProof" className="mt-3 bg-[#797979] text-white px-4 py-1 rounded-md cursor-pointer text-sm md:text-base">
                    {legalDocsFiles.businessInsuranceProof || existingDocUrls.businessInsuranceProof ? t('legalDocs.changeFile') : t('legalDocs.uploadFile')}
                  </label>
                </div>
              </div>
            </div>
            <button type="submit" className="mt-4 bg-gradient-to-r from-[#6E00FF] to-[#A627FF] text-white px-6 py-2 rounded-lg">
              {t('common.saveChanges')}
            </button>
          </div>
         </form>
      )}

      {activeTab === 'Subscription Overview' && (
        <div className="rounded-lg shadow-lg p-4 md:p-6 mt-4">
          <h2 className="text-lg text-white font-semibold mb-4">{t('subscription.title')}</h2>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium text-white">{t('subscription.currentPlan')}</label>
              <input
                type="text"
                // Change: Use subscriptionData state
                value={subscriptionData.planName}
                readOnly
                className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />

              <label className="block text-sm font-medium text-white mt-4">{t('subscription.status')}</label>
              <input
                type="text"
                // Change: Check activePackageId to decide if we show status
                value={subscriptionData.activePackageId ? t(`status.${subscriptionData.status}`, subscriptionData.status) : "-"}
                readOnly
                className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />

              <label className="block text-sm font-medium text-white mt-4">{t('subscription.nextBilling')}</label>
              <input
                type="text"
                value={subscriptionData.activePackageId ? subscriptionData.nextBillingDate : "-"} readOnly
                className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />

              <label className="block text-sm font-medium text-white mt-4">{t('subscription.paymentMethod')}</label>
              <input
                type="text"
                value={subscriptionData.activePackageId ? subscriptionData.paymentMethod : "-"}
                readOnly
                className="mt-1 w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />

              {/* Show Cancel button if a package exists */}
              {subscriptionData.activePackageId ? (
                <button
                  onClick={handleCancelSubscription}
                  className="mt-6 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md transition"
                >
                  {t('subscription.cancelPlan')}
                </button>
              ) : null}

            </div>
            
            {/* Right Side Card: Show if activePackageId exists */}
            {subscriptionData.activePackageId && (
              <div
                style={{
                  background: 'radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)',
                }}
                className="relative text-white rounded-xl p-6 w-full md:w-80 shadow-lg mt-6 md:mt-0"
              >
                <img
                  src={badge}
                  alt="badge"
                  className="absolute top-0 right-0 transform translate-x-1/3 -translate-y-1/3 w-[150px] h-[150px] md:w-[210px] md:h-[210px]"
                />
                <h3 className="text-xl font-bold">{subscriptionData.planName}</h3>
                <p className="text-4xl font-extrabold text-[#52A2F1] mt-1">
                  ${subscriptionData.price}{' '}
                  <span className="text-base font-medium text-white">{t('subscription.pricePerYear')}</span>
                </p>
                <p className="text-sm text-gray-300 mt-3 line-clamp-3">{subscriptionData.planDescription}</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CompanyProfile;